import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
} from "lucide-react";
import Headroom from "react-headroom";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import "./css/SelectSpecialty.css";

import { ClinicScheduleStatusDto } from "../web-api-client";
import HorizontalCard from "../components/booking/room/HorizontalCard";
import ClinicDetailModal from "../components/booking/ClinicDetail";
import { useIsMobile } from "../hooks/use-mobile";
import HorizontalCardSkeleton from "../components/booking/room/HorizontalCardSkeleton";
import ErrorRetry from "../components/ui/ErrorRetry";
import { scrollToTop } from "../utils/scrollToTop";

import NonScheduleDialog from "../components/NoScheduleDialog";
import { normalize } from "../utils/normalize";
import { usePagedClinics } from "../hooks/useClinics";
import { useStickyLoading } from "../hooks/useStickyLoading";
import { useDebounce } from "../hooks/useDebounce";
import { useBookingTimeGuard } from "../hooks/useBookingTimeGuard";
import BookingTimeoutDialog from "../components/booking/BookingTimeoutDialog";

const SelectSpecialty = () => {
  type ModalType = "submit" | "detail";
  const PAGE_SIZE = 20;
  const [isMobile] = useIsMobile();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const { checkBookingTime, dialogOpen, dialogType, closeDialog } =
    useBookingTimeGuard();

  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);

  const didMountRef = useRef(false);
  const didSelectRef = useRef(false);

  const jumpInputRef = useRef<HTMLInputElement | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const [selectedClinic, setSelectedClinic] =
    useState<ClinicScheduleStatusDto | null>(null);
  const [modalType, setModalType] = useState<string | "">("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(urlPage);
  const [mobileClinics, setMobileClinics] = useState<ClinicScheduleStatusDto[]>(
    [],
  );
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null);
  const [jumpOpen, setJumpOpen] = useState(false);
  const [jumpValue, setJumpValue] = useState<string>("");
  const [hasError] = useState(false);
  const [isEmptySlot, setIsEmptySlot] = useState(false);
  const [emptyDataMessage, setEmptyDataMessage] = useState("");

  const {
    data: clinicsPayload,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = usePagedClinics({
    pageNumber,
    pageSize: PAGE_SIZE,
    searchTerm: debouncedSearchTerm,
  });

  const serverClinics = clinicsPayload?.clinics ?? [];
  const totalCount = clinicsPayload?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const hasNextPage = clinicsPayload?.hasNextPage ?? false;

  const visibleClinics = isMobile ? mobileClinics : serverClinics;

  const handleClinicAction = (
    clinic: ClinicScheduleStatusDto,
    type: ModalType,
  ) => {
    setSelectedClinic(clinic ?? null);
    setModalType(type);
    setIsModalOpen(true);
    if (type === "submit") {
      onSelectSpecialty(clinic);
    }
  };

  const commitJump = () => {
    const n = Math.floor(Number(jumpValue));
    if (!Number.isFinite(n)) {
      setJumpOpen(false);
      return;
    }

    const clamped = Math.min(totalPages, Math.max(1, n));
    setJumpOpen(false);

    window.location.href = buildPageHref(clamped);
  };

  const onModalTrigger = (open: boolean) => {
    if (!open) {
      setSelectedClinic(null);
    }
    setIsModalOpen(open);
  };

  const onSelectSpecialty = (specialty: ClinicScheduleStatusDto) => {
    if (!checkBookingTime()) {
      return;
    }

    scrollToTop();

    sessionStorage.clear();

    const params = new URLSearchParams({
      type: "clinic",
      specialty: specialty.clinicId?.toString() || "",
      step: isMobile ? "chooseTime" : "chooseDoctor",
    });

    setTimeout(() => {
      navigate(`/dat-hen?${params.toString()}`);
    }, 0);
  };

  const overlayVisible = useStickyLoading(isLoading, {
    showDelay: 100,
    minVisible: 300,
  });

  const showInitialSkeleton = overlayVisible && visibleClinics.length === 0;

  useEffect(() => {
    document.title = t("danhsachphongkham");
  }, [t]);

  const loc = useLocation();
  const buildPageHref = (nextPage: number) => {
    const p = Math.max(1, Math.min(totalPages, nextPage));
    const sp = new URLSearchParams(searchParams);

    if (p <= 1) sp.delete("page");
    else sp.set("page", String(p));

    const qs = sp.toString();
    return qs ? `${loc.pathname}?${qs}` : loc.pathname;
  };

  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    setPageNumber(1);
    setMobileClinics([]);
    setIsFetchingNextPage(false);

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearchTerm, setSearchParams]);

  useEffect(() => {
    setPageNumber(urlPage);
  }, [urlPage]);

  useEffect(() => {
    if (isMobile === undefined) return;

    if (!isMobile) {
      setMobileClinics([]);
      return;
    }

    if (pageNumber === 1) {
      setMobileClinics(serverClinics);
      return;
    }

    setMobileClinics((prev) => {
      const existing = new Set(prev.map((c) => String(c.clinicId ?? "")));
      const next = serverClinics.filter(
        (c) => !existing.has(String(c.clinicId ?? "")),
      );
      return [...prev, ...next];
    });
  }, [isMobile, pageNumber, serverClinics]);

  useEffect(() => {
    if (!isMobile) return;
    if (!sentinelEl) return;
    if (!hasNextPage) return;
    if (isFetchingNextPage) return;
    if (isFetching) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        if (isFetchingNextPage) return;
        if (isFetching) return;
        if (!hasNextPage) return;

        setIsFetchingNextPage(true);
        setPageNumber((prev) => prev + 1);
      },
      { rootMargin: "300px" },
    );

    io.observe(sentinelEl);
    return () => io.disconnect();
  }, [isMobile, sentinelEl, hasNextPage, isFetchingNextPage, isFetching]);

  useEffect(() => {
    if (!isMobile) return;
    if (!isFetching) {
      setIsFetchingNextPage(false);
    }
  }, [isMobile, isFetching]);

  useEffect(() => {
    if (isMobile) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (pageNumber <= 1) next.delete("page");
        else next.set("page", String(pageNumber));
        return next;
      },
      { replace: true },
    );

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pageNumber, setSearchParams, isMobile]);

  useEffect(() => {
    if (jumpOpen) {
      setJumpValue(String(pageNumber));
      setTimeout(() => jumpInputRef.current?.focus(), 0);
    }
  }, [jumpOpen, pageNumber]);

  useEffect(() => {
    setIsEmptySlot(emptyDataMessage !== "");
  }, [emptyDataMessage]);

  useEffect(() => {
    if (isEmptySlot) {
      setIsModalOpen(false);
    } else {
      setEmptyDataMessage("");
    }
  }, [isEmptySlot]);

  return (
    <div className="min-h-screen max-w-5xl w-full mx-auto ">
      <Headroom
        className={` z-10 
          ${searchTerm ? "always-visible" : ""}
          ${isMobile ? "headroom-custom-mobile" : "headroom-custom-desktop"}`}
      >
        <div className="flex w-full items-center justify-center md:p-4 p-3 bg-white shadow-lg">
          <div className="relative w-full max-w-3xl ">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4 md:w-5 md:h-5" />
            <input
              type="text"
              placeholder={t("timkiemchuyenkhoa")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full max-w-3xl pl-12 pr-4 md:py-3 py-2 rounded-lg border border-slate-200 focus:border-green-200 focus:ring-1 focus:ring-green-500 no-outline transition-all duration-200"
            />
          </div>
        </div>
      </Headroom>

      <div className="mx-auto px-4 py-6 md:py-8">
        {showInitialSkeleton ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {Array.from({ length: 6 }).map((_, index) => (
              <HorizontalCardSkeleton key={index} />
            ))}
          </div>
        ) : isError && !isLoading ? (
          <div className="flex justify-center">
            <ErrorRetry onRetry={refetch} />
          </div>
        ) : null}

        {/* ds Chuyen khoa */}
        {visibleClinics.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {visibleClinics.map((clinic: any, index: number) => (
              <HorizontalCard
                isMobile={isMobile ?? false}
                key={`${clinic.clinicId}-${index}`}
                clinic={clinic}
                onSubmitClick={(clinic) => handleClinicAction(clinic, "submit")}
                onContainerClick={(clinic) =>
                  handleClinicAction(clinic, "detail")
                }
              />
            ))}
            {isMobile && hasNextPage && (
              <div ref={setSentinelEl} className="h-2" />
            )}
          </div>
        )}

        {!isMobile && totalPages > 1 && (
          <div className="mt-16 mb-8 flex items-center justify-center gap-2">
            <a
              className={`flex items-center h-9 px-3 rounded-lg border bg-white text-slate-700 hover:bg-slate-50 transition ${
                pageNumber <= 1 && "hidden"
              }`}
              href={buildPageHref(1)}
              title={t("trangdau")}
            >
              <ChevronsLeft className="w-4 h-4" />
            </a>

            <a
              className={`flex items-center justify-center h-9 px-3 rounded-lg border bg-primary text-white transition ${
                pageNumber <= 1 && "hidden"
              }`}
              href={buildPageHref(pageNumber - 1)}
              title={t("trangtruoc")}
            >
              <ChevronLeft className="w-4 h-4" /> {t("truoc")}
            </a>

            {!jumpOpen ? (
              <div
                onClick={() => setJumpOpen(true)}
                className="h-9 px-3 transition flex items-center gap-1 text-blue-700 cursor-pointer"
              >
                <span className="font-semibold text-blue-600">
                  {pageNumber}
                </span>
                <span className="text-blue-600">/</span>
                <span className="font-medium">{totalPages}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-2 text-blue-700">
                <input
                  ref={jumpInputRef}
                  value={jumpValue}
                  onFocus={(e) => {
                    if (!didSelectRef.current) {
                      e.currentTarget.select();
                      didSelectRef.current = true;
                    }
                  }}
                  onClick={(e) => {
                    if (!didSelectRef.current) {
                      e.currentTarget.select();
                      didSelectRef.current = true;
                    }
                  }}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d]/g, "");
                    setJumpValue(v);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitJump();
                    if (e.key === "Escape") setJumpOpen(false);
                  }}
                  onBlur={() => {
                    didSelectRef.current = false;
                    setJumpOpen(false);
                  }}
                  className="h-7 w-10 rounded-md border border-slate-200 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  inputMode="numeric"
                  placeholder={`${pageNumber}`}
                />
                <span className="text-slate-400 text-sm">/</span>
                <span className="text-sm font-medium">{totalPages}</span>

                <button
                  className="h-7 px-2 bg-white text-blue-600 rounded-md hover:bg-blue-100 transition"
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={commitJump}
                >
                  {t("toi")}
                </button>
              </div>
            )}

            <a
              className={`flex items-center justify-center h-9 px-3 rounded-lg border bg-primary text-white transition ${
                pageNumber >= totalPages && "hidden"
              }`}
              href={buildPageHref(pageNumber + 1)}
              title={t("trangsau")}
            >
              {t("tiep")} <ChevronRight className="w-4 h-4" />
            </a>

            <a
              className={`flex items-center h-9 px-3 rounded-lg border bg-white text-slate-700 hover:bg-slate-50 transition ${
                pageNumber >= totalPages && "hidden"
              }`}
              href={buildPageHref(totalPages)}
              title={t("trangcuoi")}
            >
              <ChevronsRight className="w-4 h-4" />
            </a>
          </div>
        )}

        {totalCount === 0 && !isLoading && !isError && (
          <div className="text-center py-12">
            <div className="rounded-full flex items-center justify-center mx-auto mb-4">
              <img className="h-32 md:h-48" src="/images/no-doctor.png" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">
              {t("khongtimthayphongkham")}
            </h3>
          </div>
        )}

        {isMobile && isFetchingNextPage && hasNextPage && (
          <div className="pb-4">
            <HorizontalCardSkeleton />
          </div>
        )}

        <div className="h-[90px]" />
      </div>

      <ClinicDetailModal
        type={modalType}
        clinic={selectedClinic}
        isOpen={isModalOpen}
        onTrigger={onModalTrigger}
        onRegister={(clinic) => handleClinicAction(clinic, "submit")}
      />

      <NonScheduleDialog
        open={isEmptySlot}
        onOpenChange={setIsEmptySlot}
        message={emptyDataMessage}
      />

      <BookingTimeoutDialog
        isOpen={dialogOpen}
        type={dialogType}
        onClose={closeDialog}
      />
    </div>
  );
};

export default SelectSpecialty;
